import { useState, useEffect } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface CloneStepDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newStepName: string) => void;
  originalStepName?: string;
  onCloseAutoFocus?: (event: Event) => void;
}

export function CloneStepDialog({
  isOpen,
  onClose,
  onConfirm,
  originalStepName = "Step",
  onCloseAutoFocus,
}: CloneStepDialogProps) {
  const [newStepName, setNewStepName] = useState(`${originalStepName} (clone)`);

  useEffect(() => {
    if (isOpen) {
      setNewStepName(`${originalStepName} (clone)`);
    }
  }, [originalStepName, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newStepName.trim()) {
      onConfirm(newStepName.trim());
      setNewStepName(`${originalStepName} (clone)`);
      onClose();
    }
  };

  const handleClose = () => {
    setNewStepName(`${originalStepName} (clone)`);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle className="text-white">Clone Step</DialogTitle>
          <DialogDescription className="sr-only">
            Enter a name for the cloned step and confirm to create a copy.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="py-4">
            <Label htmlFor="cloneName" className="text-white">
              New Step Name
            </Label>
            <Input
              id="cloneName"
              value={newStepName}
              onChange={(e) => setNewStepName(e.target.value)}
              className="mt-2 bg-surface-input border-surface-border text-white placeholder:text-muted focus:ring-primary"
              placeholder="Enter new step name"
              autoFocus
            />
          </div>
          <DialogFooter>
            {/* Route Cancel through Radix's Close primitive — see
                clone-persona-dialog for the DEF-792-02 rationale. */}
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                className="bg-surface border border-surface-border hover:bg-surface-hover hover:border-surface-border text-white"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-primary hover:bg-primary-dark text-white"
              disabled={!newStepName.trim()}
            >
              Clone Step
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}